import { AUDIO_RTP_TICKS_PER_MS } from "./capture-clock";
import { MAX_HOLD_MS } from "./frame-schedule";

/**
 * Ce qui sépare la prise d'un son de sa remise par le récepteur, mesuré.
 *
 * # Pourquoi une mesure
 *
 * Le moteur audio retarde chaque son reçu pour qu'il sonne avec les notes : sa
 * date de prise plus la retenue. Il le retarde **après** le décodage, et doit
 * donc savoir ce que le son a déjà pris en route : le trajet, et le tampon de
 * réception de WebRTC, qui grandit et rétrécit avec la gigue du réseau. Supposé
 * fixe, ce retard ferait tomber la voix à côté des notes à chaque saute du
 * réseau.
 *
 * # Comment
 *
 * Le récepteur dit, par `getSynchronizationSources`, l'horodatage RTP du dernier
 * son qu'il a remis, et quand. Le worker dit, de loin en loin, l'instant de
 * séance porté par une trame reçue et son horodatage RTP
 * (`media-sync.worker.ts`). L'horloge RTP d'Opus avançant de 48 000 par seconde
 * de prise, l'instant de prise de toute trame du même flux s'en déduit.
 *
 * La médiane des derniers relevés : un relevé tombé entre deux remises ne
 * déplace rien.
 */

/** Combien de relevés entrent dans la médiane : deux secondes, au rythme du moteur audio. */
export const LAG_WINDOW = 8;

/**
 * Au plus un relevé tous les tant de millisecondes de remise. Lire plus souvent
 * — une sonde, un moteur qui recalcule — ne doit ni compter deux fois la même
 * remise, ni rétrécir la fenêtre de la médiane.
 */
export const MIN_READING_INTERVAL_MS = 200;

/**
 * Au-delà, en ms, une date reçue est trop loin du son remis pour servir : deux
 * trames d'un même flux ne s'éloignent pas autant sans une coupure qui aurait
 * remis son horloge RTP ailleurs.
 */
export const MAX_SAMPLE_DISTANCE_MS = 60_000;

/**
 * En deçà, en ms, un instant de remise est compté depuis l'origine de la page ;
 * au-delà, c'est une heure Unix — 2001 en millisecondes. Voir `commonTimeOf`.
 */
const WALL_CLOCK_THRESHOLD_MS = 1e12;

const RTP_WRAP = 2 ** 32;
const RTP_HALF_WRAP = 2 ** 31;

/** Le dernier son remis par un récepteur. */
export interface DeliveredFrame {
  /** Le flux qui l'a porté (`synchronizationSource`). */
  readonly source: number;
  readonly rtpTimestamp: number;
  /** L'instant de sa remise, sur l'horloge commune. */
  readonly deliveredAtMs: number;
}

interface StampSample {
  ssrc: number;
  rtpTimestamp: number;
  sessionMs: number;
}

export class AudioLag {
  private sample: StampSample | null = null;
  private readonly lags: number[] = [];
  /** L'instant de remise du dernier relevé retenu, sur l'horloge commune. */
  private readingAtMs = Number.NEGATIVE_INFINITY;

  /** Le retard tenu, pour la sonde de débogage ; `null` tant que rien n'est mesuré. */
  get lastMs(): number | null {
    return median(this.lags);
  }

  /**
   * Une trame reçue portait cet instant de séance. Un autre flux — une piste
   * suivante sur le même récepteur — efface les relevés de l'ancien : ils
   * décrivaient un autre trajet.
   */
  record(ssrc: number, rtpTimestamp: number, sessionMs: number): void {
    if (this.sample !== null && !sameStream(this.sample.ssrc, ssrc)) {
      this.lags.length = 0;
      this.readingAtMs = Number.NEGATIVE_INFINITY;
    }

    this.sample = { ssrc, rtpTimestamp, sessionMs };
  }

  /**
   * Relève le retard du dernier son remis, et rend la médiane des derniers
   * relevés ; `null` tant qu'aucun n'a pu l'être.
   *
   * Rien ne se relève sans horloge de séance (`epochOffsetMs`), sans date reçue
   * du même flux, sur une remise trop proche du relevé précédent, ou sur une
   * valeur hors de toute retenue possible : une date aberrante ne doit pas
   * déplacer le son.
   */
  read(delivered: DeliveredFrame | null, epochOffsetMs: number | null): number | null {
    const sample = this.sample;

    if (delivered === null || sample === null || epochOffsetMs === null || !sameStream(delivered.source, sample.ssrc)) {
      return median(this.lags);
    }

    if (delivered.deliveredAtMs - this.readingAtMs < MIN_READING_INTERVAL_MS) {
      return median(this.lags);
    }

    const stepMs = rtpStep(sample.rtpTimestamp, delivered.rtpTimestamp) / AUDIO_RTP_TICKS_PER_MS;

    if (Math.abs(stepMs) > MAX_SAMPLE_DISTANCE_MS) {
      return median(this.lags);
    }

    const capturedAtMs = sample.sessionMs + stepMs - epochOffsetMs;
    const lagMs = delivered.deliveredAtMs - capturedAtMs;

    if (lagMs >= 0 && lagMs <= MAX_HOLD_MS) {
      this.readingAtMs = delivered.deliveredAtMs;
      this.lags.push(lagMs);

      if (this.lags.length > LAG_WINDOW) {
        this.lags.shift();
      }
    }

    return median(this.lags);
  }
}

/**
 * L'instant d'une remise, sur l'horloge commune d'une page et de son worker.
 *
 * La norme date `getSynchronizationSources` depuis l'origine de la page ;
 * Chromium 148 le date sur l'horloge murale, en millisecondes Unix. On reconnaît
 * l'une à sa grandeur. L'horloge murale est ramenée sur la commune au moment du
 * relevé : les deux s'écartent au fil d'une séance, quand le système recale son
 * heure.
 */
export function commonTimeOf(timestamp: number, dateNowMs: number, commonNowMs: number, timeOrigin: number): number {
  if (timestamp > WALL_CLOCK_THRESHOLD_MS) {
    return timestamp - (dateNowMs - commonNowMs);
  }

  return timeOrigin + timestamp;
}

/**
 * Deux relevés parlent-ils du même flux ? Un flux sans identifiant — la
 * métadonnée manquait, et le worker rapporte 0 — vaut pour n'importe lequel :
 * sans quoi la mesure ne démarrerait jamais.
 */
function sameStream(a: number, b: number): boolean {
  return a === 0 || b === 0 || a === b;
}

/** Ce qui sépare deux horodatages RTP, passage par zéro compris. */
function rtpStep(from: number, to: number): number {
  let step = to - from;

  if (step >= RTP_HALF_WRAP) {
    step -= RTP_WRAP;
  } else if (step < -RTP_HALF_WRAP) {
    step += RTP_WRAP;
  }

  return step;
}

function median(values: readonly number[]): number | null {
  if (values.length === 0) {
    return null;
  }

  const sorted = [...values].sort((a, b) => a - b);

  return sorted[Math.floor(sorted.length / 2)];
}
